import type {
  GuildWarBalancedRosterBoard,
  GuildWarDualRosterBoard,
  GuildWarDualTeamRoster,
  GuildWarFlatTeamRoster,
  GuildWarFlatRosterBoard,
  GuildWarFlatTeamWithViceRoster,
  GuildWarRosterBoard,
  GuildWarRosterBoardRecord,
  GuildWarRosterMemberPathSnapshot,
  GuildWarRosterSlotTarget,
  GuildWarRosterTeamKey,
  GuildWarSpecializedMobilityRoster,
  GuildWarSquadTeamRoster,
  GuildWarTeamRoster,
  GuildWarThreeTeamRosterBoard,
} from "./types.js"

const cloneGuildWarRosterMemberPaths = (
  paths: Record<string, GuildWarRosterMemberPathSnapshot>,
) => {
  const entries = Object.entries(paths).map(([memberId, snapshot]) => [
    memberId,
    {
      primaryPathCode: snapshot.primaryPathCode,
      secondaryPathCode: snapshot.secondaryPathCode,
    },
  ])

  return Object.fromEntries(entries) as Record<
    string,
    GuildWarRosterMemberPathSnapshot
  >
}

export const GUILD_WAR_ROSTER_FORMATS = [
  "specialized",
  "balanced",
  "dual",
] as const

export type GuildWarRosterFormat = (typeof GUILD_WAR_ROSTER_FORMATS)[number]

export const GUILD_WAR_ROSTER_FORMAT_LABELS = {
  specialized: "Specialized: Meowbility 5 / Nyanttack 15 / Defence 10",
  balanced: "Balanced: Meowbility 10 / Nyanttack 10 / Defence 10",
  dual: "Dual: Nyanttack 15 / Defence 15",
} satisfies Record<GuildWarRosterFormat, string>

export function createEmptyGuildWarTeamRoster(
  team: "mobility",
): GuildWarSpecializedMobilityRoster
export function createEmptyGuildWarTeamRoster(
  team: "attack" | "defense",
): GuildWarFlatTeamWithViceRoster
export function createEmptyGuildWarTeamRoster(
  team: GuildWarRosterTeamKey,
): GuildWarFlatTeamRoster
export function createEmptyGuildWarTeamRoster(
  team: GuildWarRosterTeamKey,
): GuildWarFlatTeamRoster {
  if (team === "mobility") {
    return {
      commander: null,
      viceCommander: null,
      members: [null, null, null],
    }
  }

  return {
    commander: null,
    viceCommander: null,
    members: Array(team === "attack" ? 13 : 8).fill(null),
  }
}

export const createEmptyGuildWarRosterBoard = (): GuildWarFlatRosterBoard => ({
  mobility: createEmptyGuildWarTeamRoster("mobility"),
  attack: createEmptyGuildWarTeamRoster("attack"),
  defense: createEmptyGuildWarTeamRoster("defense"),
  memberPaths: {},
  memberDisplayNames: {},
  commanders: [null, null, null, null, null],
  halftimePerformers: [null, null, null],
})

const createEmptyGuildWarSquadTeamRoster = (): GuildWarSquadTeamRoster => ({
  commander: null,
  viceCommander: null,
  subteams: [
    { key: "alpha", members: Array(4).fill(null) },
    { key: "beta", members: Array(4).fill(null) },
  ],
})

export const createEmptyBalancedGuildWarRosterBoard =
  (): GuildWarBalancedRosterBoard => ({
    mobility: createEmptyGuildWarSquadTeamRoster(),
    attack: createEmptyGuildWarSquadTeamRoster(),
    defense: createEmptyGuildWarSquadTeamRoster(),
    memberPaths: {},
    memberDisplayNames: {},
    commanders: [null, null, null, null, null],
    halftimePerformers: [null, null, null],
  })

const createEmptyGuildWarDualTeamRoster = (): GuildWarDualTeamRoster => ({
  commander: null,
  viceCommander: null,
  members: [
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ],
})

export const createEmptyDualGuildWarRosterBoard =
  (): GuildWarDualRosterBoard => ({
    attack: createEmptyGuildWarDualTeamRoster(),
    defense: createEmptyGuildWarDualTeamRoster(),
    memberPaths: {},
    memberDisplayNames: {},
    commanders: [null, null, null, null, null],
    halftimePerformers: [null, null, null],
  })

const hasExactOwnKeys = (
  value: object,
  allowedKeys: readonly PropertyKey[],
) => {
  const ownKeys = Reflect.ownKeys(value)
  return (
    ownKeys.length === allowedKeys.length &&
    ownKeys.every((key) => allowedKeys.includes(key))
  )
}

const isRosterSlot = (value: unknown): value is string | null =>
  value === null || typeof value === "string"

const isObject = (value: unknown): value is Record<PropertyKey, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value)

const hasExactRosterSlots = (
  values: unknown[],
  expectedLength: number,
): values is Array<string | null> => {
  if (values.length !== expectedLength) return false
  for (let index = 0; index < expectedLength; index += 1) {
    if (!Object.hasOwn(values, index) || !isRosterSlot(values[index])) {
      return false
    }
  }
  return true
}

const isExactFlatTeam = (team: unknown, expectedMemberCount: number) =>
  isObject(team) &&
  hasExactOwnKeys(team, ["commander", "viceCommander", "members"]) &&
  Array.isArray(team.members) &&
  hasExactRosterSlots(team.members, expectedMemberCount) &&
  isRosterSlot(team.commander) &&
  isRosterSlot(team.viceCommander)

const isExactSquadTeam = (team: unknown) => {
  if (
    !isObject(team) ||
    !hasExactOwnKeys(team, ["commander", "viceCommander", "subteams"]) ||
    !isRosterSlot(team.commander) ||
    !isRosterSlot(team.viceCommander) ||
    !Array.isArray(team.subteams) ||
    team.subteams.length !== 2
  ) {
    return false
  }

  return team.subteams.every(
    (subteam, index) =>
      isObject(subteam) &&
      hasExactOwnKeys(subteam, ["key", "members"]) &&
      subteam.key === (index === 0 ? "alpha" : "beta") &&
      Array.isArray(subteam.members) &&
      hasExactRosterSlots(subteam.members, 4),
  )
}

export const isSpecializedGuildWarRosterBoard = (
  board: unknown,
): board is GuildWarFlatRosterBoard =>
  isObject(board) &&
  Object.hasOwn(board, "mobility") &&
  Object.hasOwn(board, "attack") &&
  Object.hasOwn(board, "defense") &&
  isExactFlatTeam(board.mobility, 3) &&
  isExactFlatTeam(board.attack, 13) &&
  isExactFlatTeam(board.defense, 8)

export const isBalancedGuildWarRosterBoard = (
  board: unknown,
): board is GuildWarBalancedRosterBoard =>
  isObject(board) &&
  Object.hasOwn(board, "mobility") &&
  Object.hasOwn(board, "attack") &&
  Object.hasOwn(board, "defense") &&
  isExactSquadTeam(board.mobility) &&
  isExactSquadTeam(board.attack) &&
  isExactSquadTeam(board.defense)

const isExactRosterTuple = (
  value: unknown,
  length: number,
): value is Array<string | null> =>
  Array.isArray(value) && hasExactRosterSlots(value, length)

export const isDualGuildWarRosterBoard = (
  board: unknown,
): board is GuildWarDualRosterBoard =>
  isObject(board) &&
  hasExactOwnKeys(board, [
    "attack",
    "defense",
    "memberPaths",
    "memberDisplayNames",
    "commanders",
    "halftimePerformers",
  ]) &&
  isExactFlatTeam(board.attack, 13) &&
  isExactFlatTeam(board.defense, 13) &&
  isObject(board.memberPaths) &&
  isObject(board.memberDisplayNames) &&
  isExactRosterTuple(board.commanders, 5) &&
  isExactRosterTuple(board.halftimePerformers, 3)

export const getGuildWarRosterFormat = (
  board: unknown,
): GuildWarRosterFormat | null =>
  isSpecializedGuildWarRosterBoard(board)
    ? "specialized"
    : isBalancedGuildWarRosterBoard(board)
      ? "balanced"
      : isDualGuildWarRosterBoard(board)
        ? "dual"
        : null

const isStructuralTeam = (team: unknown): team is GuildWarTeamRoster =>
  isObject(team) &&
  isRosterSlot(team.commander) &&
  (Array.isArray(team.members) ||
    (Array.isArray(team.subteams) &&
      team.subteams.length === 2 &&
      team.subteams.every(
        (subteam) => isObject(subteam) && Array.isArray(subteam.members),
      )))

export const getGuildWarRosterTeamKeys = (
  value: unknown,
): GuildWarRosterTeamKey[] => {
  if (!isObject(value)) return []
  return (["mobility", "attack", "defense"] as const).filter(
    (key) => Object.hasOwn(value, key) && isStructuralTeam(value[key]),
  )
}

export const isGuildWarFlatTeamRoster = (
  team: GuildWarTeamRoster,
): team is GuildWarFlatTeamRoster => "members" in team

export const hasGuildWarViceCommanderSlot = (
  team: GuildWarTeamRoster,
): team is GuildWarFlatTeamWithViceRoster | GuildWarSquadTeamRoster =>
  "viceCommander" in team && team.viceCommander !== undefined

export const getGuildWarTeamCapacity = (team: GuildWarTeamRoster): number =>
  1 +
  (hasGuildWarViceCommanderSlot(team) ? 1 : 0) +
  (isGuildWarFlatTeamRoster(team)
    ? team.members.length
    : team.subteams[0].members.length + team.subteams[1].members.length)

export const getGuildWarTeamMemberSlots = (
  team: GuildWarTeamRoster,
): Array<string | null> =>
  isGuildWarFlatTeamRoster(team)
    ? [...team.members]
    : [...team.subteams[0].members, ...team.subteams[1].members]

export const getGuildWarTeamRosterMembers = (
  team: GuildWarTeamRoster,
): string[] => {
  const members: string[] = []
  if (team.commander !== null) members.push(team.commander)
  const viceCommander = "viceCommander" in team ? team.viceCommander : undefined
  if (viceCommander != null) members.push(viceCommander)

  if (isGuildWarFlatTeamRoster(team)) {
    for (const member of team.members) {
      if (member !== null) members.push(member)
    }
  } else {
    for (const subteam of team.subteams) {
      for (const member of subteam.members) {
        if (member !== null) members.push(member)
      }
    }
  }

  return members
}

export const cloneGuildWarTeamRoster = (
  team: GuildWarTeamRoster,
): GuildWarTeamRoster => {
  if (isGuildWarFlatTeamRoster(team)) {
    return "viceCommander" in team
      ? {
          commander: team.commander,
          viceCommander: team.viceCommander ?? null,
          members: [...team.members],
        }
      : { commander: team.commander, members: [...team.members] }
  }

  return {
    commander: team.commander,
    viceCommander: team.viceCommander,
    subteams: team.subteams.map((subteam) => ({
      key: subteam.key,
      members: [...subteam.members],
    })) as GuildWarSquadTeamRoster["subteams"],
  }
}

export function cloneGuildWarRosterBoard(
  board: GuildWarDualRosterBoard,
): GuildWarDualRosterBoard
export function cloneGuildWarRosterBoard(
  board: GuildWarThreeTeamRosterBoard,
): GuildWarThreeTeamRosterBoard
export function cloneGuildWarRosterBoard(
  board: GuildWarRosterBoard,
): GuildWarRosterBoard
export function cloneGuildWarRosterBoard(
  board: GuildWarRosterBoard,
): GuildWarRosterBoard {
  const metadata = {
    memberPaths: cloneGuildWarRosterMemberPaths(board.memberPaths ?? {}),
    memberDisplayNames: { ...board.memberDisplayNames },
    commanders: [...board.commanders] as GuildWarRosterBoard["commanders"],
    halftimePerformers: [
      ...board.halftimePerformers,
    ] as GuildWarRosterBoard["halftimePerformers"],
  }
  if (!Object.hasOwn(board, "mobility")) {
    return {
      attack: cloneGuildWarTeamRoster(board.attack) as GuildWarDualTeamRoster,
      defense: cloneGuildWarTeamRoster(board.defense) as GuildWarDualTeamRoster,
      ...metadata,
    }
  }
  const threeTeamBoard = board as GuildWarThreeTeamRosterBoard
  return {
    mobility: cloneGuildWarTeamRoster(threeTeamBoard.mobility),
    attack: cloneGuildWarTeamRoster(threeTeamBoard.attack),
    defense: cloneGuildWarTeamRoster(threeTeamBoard.defense),
    ...metadata,
  }
}

type GuildWarRosterTeamPosition = Extract<
  GuildWarRosterSlotTarget,
  { kind: "team-role" | "team-member" | "subteam-member" }
>

type GuildWarRosterPositionMapping = Readonly<
  Record<GuildWarRosterFormat, GuildWarRosterTeamPosition>
>

const role = (
  team: GuildWarRosterTeamKey,
  slot: "commander" | "viceCommander",
): GuildWarRosterTeamPosition => ({ kind: "team-role", team, role: slot })

const member = (
  team: GuildWarRosterTeamKey,
  index: number,
): GuildWarRosterTeamPosition => ({ kind: "team-member", team, index })

const squadMember = (
  team: GuildWarRosterTeamKey,
  squad: "alpha" | "beta",
  index: number,
): GuildWarRosterTeamPosition => ({
  kind: "subteam-member",
  team,
  squad,
  index,
})

const position = (
  specialized: GuildWarRosterTeamPosition,
  balanced: GuildWarRosterTeamPosition,
  dual: GuildWarRosterTeamPosition,
): GuildWarRosterPositionMapping => ({ specialized, balanced, dual })

const GUILD_WAR_ROSTER_POSITION_MAPPING: readonly GuildWarRosterPositionMapping[] =
  [
    position(
      role("mobility", "commander"),
      role("mobility", "commander"),
      member("defense", 8),
    ),
    position(
      role("mobility", "viceCommander"),
      squadMember("mobility", "alpha", 0),
      member("defense", 9),
    ),
    ...[0, 1, 2].map((index) =>
      position(
        member("mobility", index),
        squadMember("mobility", "alpha", index + 1),
        member("defense", index + 10),
      ),
    ),
    position(
      role("attack", "commander"),
      role("attack", "commander"),
      role("attack", "commander"),
    ),
    position(
      role("attack", "viceCommander"),
      role("attack", "viceCommander"),
      role("attack", "viceCommander"),
    ),
    ...[0, 1, 2, 3].map((index) =>
      position(
        member("attack", index),
        squadMember("attack", "alpha", index),
        member("attack", index),
      ),
    ),
    ...[0, 1, 2, 3].map((index) =>
      position(
        member("attack", index + 4),
        squadMember("attack", "beta", index),
        member("attack", index + 4),
      ),
    ),
    position(
      member("attack", 8),
      role("mobility", "viceCommander"),
      member("attack", 8),
    ),
    ...[0, 1, 2, 3].map((index) =>
      position(
        member("attack", index + 9),
        squadMember("mobility", "beta", index),
        member("attack", index + 9),
      ),
    ),
    position(
      role("defense", "commander"),
      role("defense", "commander"),
      role("defense", "commander"),
    ),
    position(
      role("defense", "viceCommander"),
      role("defense", "viceCommander"),
      role("defense", "viceCommander"),
    ),
    ...[0, 1, 2, 3].map((index) =>
      position(
        member("defense", index),
        squadMember("defense", "alpha", index),
        member("defense", index),
      ),
    ),
    ...[0, 1, 2, 3].map((index) =>
      position(
        member("defense", index + 4),
        squadMember("defense", "beta", index),
        member("defense", index + 4),
      ),
    ),
  ]

const readRosterPosition = (
  board: GuildWarRosterBoard,
  position: GuildWarRosterTeamPosition,
): string | null => {
  const team = Object.hasOwn(board, position.team)
    ? board[position.team]!
    : undefined
  if (!team) return null
  if (position.kind === "team-role") {
    return team[position.role] ?? null
  }
  if (position.kind === "team-member") {
    return isGuildWarFlatTeamRoster(team)
      ? (team.members[position.index] ?? null)
      : null
  }
  if (isGuildWarFlatTeamRoster(team)) {
    return null
  }
  const squadIndex = position.squad === "alpha" ? 0 : 1
  return team.subteams[squadIndex].members[position.index] ?? null
}

const writeRosterPosition = (
  board: GuildWarRosterBoard,
  position: GuildWarRosterTeamPosition,
  memberId: string | null,
) => {
  const team = Object.hasOwn(board, position.team)
    ? board[position.team]!
    : undefined
  if (!team) return
  if (position.kind === "team-role") {
    if (position.role === "commander") {
      team.commander = memberId
    } else if ("viceCommander" in team) {
      team.viceCommander = memberId
    }
    return
  }
  if (position.kind === "team-member") {
    if (isGuildWarFlatTeamRoster(team)) {
      team.members[position.index] = memberId
    }
    return
  }
  if (!isGuildWarFlatTeamRoster(team)) {
    const squadIndex = position.squad === "alpha" ? 0 : 1
    team.subteams[squadIndex].members[position.index] = memberId
  }
}

const sameRosterPosition = (
  left: GuildWarRosterTeamPosition,
  right: GuildWarRosterTeamPosition,
) =>
  left.kind === right.kind &&
  left.team === right.team &&
  (left.kind === "team-role"
    ? right.kind === "team-role" && left.role === right.role
    : left.kind === "team-member"
      ? right.kind === "team-member" && left.index === right.index
      : right.kind === "subteam-member" &&
        left.squad === right.squad &&
        left.index === right.index)

export type GuildWarRosterFormatMove = {
  memberId: string
  from: GuildWarRosterSlotTarget
  to: GuildWarRosterSlotTarget
}

export type GuildWarRosterFormatChange = {
  sourceBoard: GuildWarRosterBoard
  sourceFormat: GuildWarRosterFormat
  targetFormat: GuildWarRosterFormat
  convertedBoard: GuildWarRosterBoard
  moves: readonly GuildWarRosterFormatMove[]
}

export const prepareGuildWarRosterFormatChange = (
  board: GuildWarRosterBoard,
  targetFormat: GuildWarRosterFormat,
): GuildWarRosterFormatChange | null => {
  const sourceFormat = getGuildWarRosterFormat(board)
  if (sourceFormat === null || sourceFormat === targetFormat) {
    return null
  }

  const convertedBoard =
    targetFormat === "specialized"
      ? createEmptyGuildWarRosterBoard()
      : targetFormat === "balanced"
        ? createEmptyBalancedGuildWarRosterBoard()
        : createEmptyDualGuildWarRosterBoard()
  const moves: GuildWarRosterFormatMove[] = []

  for (const mapping of GUILD_WAR_ROSTER_POSITION_MAPPING) {
    const from = mapping[sourceFormat]
    const to = mapping[targetFormat]
    const memberId = readRosterPosition(board, from)
    writeRosterPosition(convertedBoard, to, memberId)
    if (memberId !== null && !sameRosterPosition(from, to)) {
      moves.push({ memberId, from: { ...from }, to: { ...to } })
    }
  }

  convertedBoard.memberPaths = cloneGuildWarRosterMemberPaths(board.memberPaths)
  convertedBoard.memberDisplayNames = { ...board.memberDisplayNames }
  convertedBoard.commanders = [
    ...board.commanders,
  ] as GuildWarRosterBoard["commanders"]
  convertedBoard.halftimePerformers = [
    ...board.halftimePerformers,
  ] as GuildWarRosterBoard["halftimePerformers"]

  return {
    sourceBoard: board,
    sourceFormat,
    targetFormat,
    convertedBoard,
    moves,
  }
}

export function clearGuildWarRosterBoard(
  board: GuildWarDualRosterBoard,
): GuildWarDualRosterBoard
export function clearGuildWarRosterBoard(
  board: GuildWarThreeTeamRosterBoard,
): GuildWarThreeTeamRosterBoard
export function clearGuildWarRosterBoard(
  board: GuildWarRosterBoard,
): GuildWarRosterBoard
export function clearGuildWarRosterBoard(
  board: GuildWarRosterBoard,
): GuildWarRosterBoard {
  const clearTeam = (team: GuildWarTeamRoster): GuildWarTeamRoster => {
    if (isGuildWarFlatTeamRoster(team)) {
      return "viceCommander" in team
        ? {
            commander: null,
            viceCommander: null,
            members: team.members.map(() => null),
          }
        : { commander: null, members: team.members.map(() => null) }
    }

    return {
      commander: null,
      viceCommander: null,
      subteams: team.subteams.map((subteam) => ({
        key: subteam.key,
        members: subteam.members.map(() => null),
      })) as GuildWarSquadTeamRoster["subteams"],
    }
  }

  const metadata = {
    memberPaths: {},
    memberDisplayNames: {},
    commanders: [
      null,
      null,
      null,
      null,
      null,
    ] as GuildWarRosterBoard["commanders"],
    halftimePerformers: [
      null,
      null,
      null,
    ] as GuildWarRosterBoard["halftimePerformers"],
  }
  if (!Object.hasOwn(board, "mobility")) {
    return {
      attack: clearTeam(board.attack) as GuildWarDualTeamRoster,
      defense: clearTeam(board.defense) as GuildWarDualTeamRoster,
      ...metadata,
    }
  }
  const threeTeamBoard = board as GuildWarThreeTeamRosterBoard
  return {
    mobility: clearTeam(threeTeamBoard.mobility),
    attack: clearTeam(threeTeamBoard.attack),
    defense: clearTeam(threeTeamBoard.defense),
    ...metadata,
  }
}

export const createEmptyGuildWarRosterBoardRecord = (input: {
  rosterDate: string
  sourceWeekId?: string | null
  createdAt: Date
  updatedAt: Date
}): GuildWarRosterBoardRecord => ({
  rosterDate: input.rosterDate,
  sourceWeekId: input.sourceWeekId ?? null,
  draft: createEmptyGuildWarRosterBoard(),
  published: null,
  version: 0,
  publishedVersion: null,
  displayDate: null,
  publishedDisplayDate: null,
  publishedAt: null,
  publishedBy: null,
  lastEditedAt: input.updatedAt,
  lastEditedBy: null,
  createdAt: input.createdAt,
  updatedAt: input.updatedAt,
})
